import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Typography
} from '@mui/material';
import { IconBuilding, IconPlus, IconSearch, IconBuildingSkyscraper } from '@tabler/icons';

import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const ViewCompanies = () => {
    const [appId, setAppId] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });

    const history = useHistory();

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarEmpresas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const listarEmpresas = async (search) => {
        try {
            const query = search ? `?search=${encodeURIComponent(search)}` : '';
            const res = await api.get(`v1/apps/${appId}/companies/${query}`);
            setCompanies(res.data.companies || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar empresas' });
        }
    };

    const debouncedSearch = useMemo(() => debounce((value) => listarEmpresas(value), 400), [appId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    return (
        <Box>
            <PageHeader
                icon={IconBuilding}
                gradient="graphite"
                title="Minha Empresa"
                subtitle="Empresas e filiais do seu próprio grupo (matriz/filiais internas)"
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            size="small"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Buscar por nome, CNPJ..."
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ width: 240 }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={() => history.push('/dashboard/config/companies/create')}
                        >
                            Adicionar empresa
                        </Button>
                    </Stack>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>CNPJ</TableCell>
                                <TableCell>Telefone</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Filiais</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {companies.map((company) => (
                                <TableRow
                                    key={company.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => history.push(`/dashboard/config/companies/edit/${company.id}`)}
                                >
                                    <TableCell>
                                        <Typography fontWeight={600}>{company.name}</Typography>
                                    </TableCell>
                                    <TableCell>{company.cnpj || '—'}</TableCell>
                                    <TableCell>{company.phone || '—'}</TableCell>
                                    <TableCell>{company.email || '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<IconBuildingSkyscraper size={14} />}
                                            label={company.branches_count || 0}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {companies.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhuma empresa cadastrada ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

function debounce(func, delay) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
}

export default ViewCompanies;
